import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Sparkles, BrainCircuit, User } from 'lucide-react';

import PricingModal from './PricingModal';

const AICoachDrawer = ({ upcomingEvents = [], athleteName, suggestedInput, setSuggestedInput, recoveryData, isOpen: externalIsOpen, onClose: externalOnClose }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen || internalIsOpen;

  const handleClose = () => {
    setInternalIsOpen(false);
    if (externalOnClose) externalOnClose();
  };

  const handleOpen = () => {
    setInternalIsOpen(true);
  };
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: `¡Hola${athleteName ? ' ' + athleteName : ''}! Soy tu entrenador de IA deportiva. He analizado tu fisiología, entrenamientos recientes y tus próximos retos. ¿En qué te ayudo hoy?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages.length, isOpen]);

  useEffect(() => {
    if (suggestedInput) {
      setInternalIsOpen(true);
      setInput(suggestedInput);
      if (setSuggestedInput) setSuggestedInput('');
    }
  }, [suggestedInput, setSuggestedInput]);

  // Lógica de Smart Prompts Contextuales
  const getSuggestedPrompts = () => {
    // Solo mostrar sugerencias si no hemos empezado a chatear (el primer mensaje de la IA no cuenta)
    if (messages.length > 1) return [];

    const prompts = [];
    
    // 1. Condición: Eventos próximos (A-Race y subsecuentes)
    const validEvents = (upcomingEvents || []).filter(e => e && e.date && new Date(e.date) >= new Date()).sort((a, b) => new Date(a.date) - new Date(b.date));
    validEvents.slice(0, 2).forEach(ev => {
      prompts.push({
        text: `¿Cómo adapto mi entreno para ${ev.name}?`,
        icon: '🎯'
      });
    });

    // 2. Condición: Readiness/Health (Si estuviera disponible en un caso real, por ahora usamos la conectividad)
    // Usaremos un prompt genérico de recuperación y sueño que la IA sabe interpretar
    prompts.push({
      text: "Analiza mi sueño y HRV, ¿qué tal he recuperado?",
      icon: '🛌'
    });

    // 3. Condición Genérica de estado TSB
    prompts.push({
      text: "Hazme un resumen de mi fatiga y estado de forma actual.",
      icon: '📊'
    });

    return prompts.slice(0, 3); // Max 3 prompts
  };

  const suggestedPrompts = getSuggestedPrompts();

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setLoading(true);

    try {
      const chatHistory = messages.map(m => ({ role: m.role, text: m.text }));
      const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');
      
      const payload = { 
        message: userMessage, 
        chatHistory, 
        upcomingEvents, 
        readinessScore: recoveryData?.readinessScore 
      };

      if (recoveryData) {
        if (recoveryData.sleep?.currentScore != null) payload.currentSleep = recoveryData.sleep.currentScore;
        if (recoveryData.hrv?.current != null) payload.currentHrv = recoveryData.hrv.current;
        if (recoveryData.rhr?.current != null) payload.currentRhr = recoveryData.rhr.current;
      }

      const response = await fetch(`${API_URL}/ai/coach`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.status === 403 && data.error === 'LIMIT_REACHED') {
        setMessages(prev => [...prev, { role: 'assistant', text: data.message, isLimitError: true }]);
        setTimeout(() => setIsPricingOpen(true), 1500); // Open modal after showing message
      } else if (response.ok) {
        setMessages(prev => [...prev, { role: 'assistant', text: data.reply }]);
      } else {
        const errorMsg = data.details ? `Error del servidor: ${data.details}` : 'Error procesando tu consulta con el servidor.';
        setMessages(prev => [...prev, { role: 'assistant', text: errorMsg }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', text: `Error de red: ${error.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* FLOATING TRIGGER BUTTON WITH PULSE */}
      <button
        className="tour-ai-coach"
        onClick={handleOpen}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 999,
          background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)',
          border: 'none',
          borderRadius: '50%',
          width: '56px',
          height: '56px',
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(0, 229, 255, 0.4), 0 0 0 0px rgba(0, 229, 255, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          animation: 'pulseBtn 2s infinite ease-in-out',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
          e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 229, 255, 0.6)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 229, 255, 0.4)';
        }}
      >
        <Sparkles size={24} />
      </button>

      {/* CSS KEYFRAMES FOR PULSE */}
      <style>{`
        @keyframes pulseBtn {
          0% {
            box-shadow: 0 8px 24px rgba(0, 229, 255, 0.4), 0 0 0 0px rgba(0, 229, 255, 0.3);
          }
          70% {
            box-shadow: 0 8px 24px rgba(0, 229, 255, 0.4), 0 0 0 12px rgba(0, 229, 255, 0);
          }
          100% {
            box-shadow: 0 8px 24px rgba(0, 229, 255, 0.4), 0 0 0 0px rgba(0, 229, 255, 0);
          }
        }
      `}</style>

      {/* BACKGROUND BACKDROP */}
      {isOpen && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(5, 8, 16, 0.4)',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.3s ease'
          }}
        />
      )}

      {/* DRAWER PANEL */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '430px',
          zIndex: 1001,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(20px)',
          borderLeft: '1px solid var(--glass-border)',
          boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }}
      >
        {/* DRAWER HEADER */}
        <div className="ai-drawer-header" style={{
          padding: 'calc(1.25rem + env(safe-area-inset-top, 0px)) 1.25rem 1.25rem 1.25rem',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.015)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.15), rgba(139, 92, 246, 0.15))',
              border: '1px solid rgba(0,229,255,0.25)',
              borderRadius: '8px',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BrainCircuit size={18} style={{ color: '#00e5ff' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800, color: 'white' }}>Asistente Deportivo IA</h3>
              <span style={{ fontSize: '0.65rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '1px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }}></span> En línea • Fisiólogo Copilot
              </span>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer',
              padding: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.color = 'white'}
            onMouseOut={(e) => e.target.style.color = 'var(--text-secondary)'}
          >
            <X size={20} />
          </button>
        </div>

        {/* DRAWER CHAT PANEL */}
        <div style={{
          flex: 1,
          padding: '1.25rem',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {messages.map((msg, idx) => {
            const isUser = msg.role === 'user';
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  maxWidth: '85%',
                  alignSelf: isUser ? 'flex-end' : 'flex-start'
                }}
              >
                {!isUser && (
                  <div style={{
                    background: 'rgba(0, 229, 255, 0.12)', border: '1px solid rgba(0, 229, 255, 0.2)',
                    borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: '2px', flexShrink: 0
                  }}>
                    <BrainCircuit size={14} style={{ color: '#00e5ff' }} />
                  </div>
                )}
                <div style={{
                  background: isUser ? 'var(--strava-orange)' : (msg.isLimitError ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255, 255, 255, 0.03)'),
                  border: isUser ? 'none' : (msg.isLimitError ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid var(--glass-border)'),
                  borderRadius: isUser ? '14px 14px 2px 14px' : '2px 14px 14px 14px',
                  padding: '0.65rem 0.85rem',
                  fontSize: '0.8rem',
                  color: msg.isLimitError ? '#fca5a5' : 'white',
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap'
                }}>
                  {msg.text}
                </div>
                {isUser && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.08)', border: '1px solid var(--glass-border)',
                    borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: '2px', flexShrink: 0
                  }}>
                    <User size={14} style={{ color: 'var(--text-secondary)' }} />
                  </div>
                )}
              </div>
            );
          })}
          {loading && (
            <div style={{ display: 'flex', gap: '0.5rem', alignSelf: 'flex-start', maxWidth: '80%', alignItems: 'center' }}>
              <div style={{
                background: 'rgba(0, 229, 255, 0.12)', border: '1px solid rgba(0, 229, 255, 0.2)',
                borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <BrainCircuit size={14} style={{ color: '#00e5ff' }} />
              </div>
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--glass-border)',
                borderRadius: '2px 14px 14px 14px', padding: '0.5rem 0.8rem', fontSize: '0.78rem', color: 'var(--text-secondary)'
              }}>
                Analizando métricas y redactando...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* CHAT INPUT AREA */}
        <div style={{ display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.01)', borderTop: '1px solid var(--glass-border)' }}>
          {/* Quick Suggestions */}
          {suggestedPrompts.length > 0 && (
            <div className="smart-prompts" style={{ padding: '0.75rem 1.25rem 0', display: 'flex', gap: '0.5rem', overflowX: 'auto', whiteSpace: 'nowrap' }}>
              {suggestedPrompts.map((prompt, idx) => (
                <button 
                  key={idx}
                  onClick={() => { setInput(prompt.text); }}
                  style={{
                    background: 'rgba(0, 229, 255, 0.1)', border: '1px solid rgba(0, 229, 255, 0.25)', color: '#00e5ff',
                    borderRadius: '12px', padding: '0.4rem 0.8rem', fontSize: '0.75rem', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(0, 229, 255, 0.2)'}
                  onMouseOut={e => e.currentTarget.style.background = 'rgba(0, 229, 255, 0.1)'}
                >
                  <span style={{ fontSize: '1rem' }}>{prompt.icon}</span> {prompt.text}
                </button>
              ))}
            </div>
          )}
          <form
            onSubmit={handleSend}
            style={{
              padding: '0.75rem 1.25rem 1.25rem',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center'
            }}
          >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Pregunta sobre tu fatiga, sueño, TSB..."
            disabled={loading}
            style={{
              flex: 1,
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--glass-border)',
              borderRadius: '8px',
              padding: '0.55rem 0.85rem',
              fontSize: '0.8rem',
              color: 'white',
              fontFamily: 'inherit',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#00e5ff'}
            onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            style={{
              background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)',
              border: 'none',
              borderRadius: '8px',
              padding: '0.55rem 0.95rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              opacity: !input.trim() || loading ? 0.5 : 1,
              transition: 'all 0.2s'
            }}
          >
            <Send size={14} />
          </button>
        </form>
        <div style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.65rem', color: 'var(--text-muted)' }}>
          ⚠️ La IA no proporciona diagnósticos médicos. Ante cualquier síntoma, consulta a un profesional.
        </div>
        </div>

      </div>
      {/* PRICING MODAL */}
      <PricingModal isOpen={isPricingOpen} onClose={() => setIsPricingOpen(false)} />
    </>
  );
};

export default AICoachDrawer;
